// The marks-roll check, said on the sheet (cca-71hp's flag; cca-5uma).
//
// The worker asks one question beside every count: does the public record of who
// voted on this ballot name anybody the roll records as having no vote
// (`may_vote = 0`) — an Officer, for example. "Having no vote", not "not entitled
// to vote": the tally's "Excluded — not entitled to vote here" row counts papers
// from the wrong House under SO 15.8, a different thing (view.ts says why). Three
// answers come back (`RollIntegrityView`), and this draws two of them:
//
//  • FOUND — the fact, the number, and who to tell. A warn `.alert`, a note said
//    beside the result, and NOT the withheld card: the result is not this
//    notice's to judge, and the card is the result's own place standing empty.
//  • UNCHECKED — the check did not complete, said in words. An info `.alert`: it
//    is not a finding, and it must not be confused with one either way.
//  • CLEAN — nothing at all. See RollIntegrityView for why the ordinary case is
//    silent, and ResultView.rollIntegrity for why that silence can only be
//    reached by a check that really ran.
//
// No `role`. The notice is part of the sheet, present when the sheet mounts, and
// the count-complete heading already takes focus on that transition
// (ScrutineerPage); a live region here would interrupt it mid-sentence — the same
// reason the sheet's header gives for never using `role="alert"` on a count.
//
// BUT FOCUS LANDS BELOW IT. On the counted panels the heading that takes focus
// comes after the whole sheet, so a screen-reader scrutineer reading forward from
// there reaches Sign and Declare without passing this. So the notice carries an
// `id` and `tabIndex={-1}` — programmatically focusable, never in the tab order —
// and the operator panel puts one line under its heading that says the roll check
// is there and takes focus to it (ScrutineerPage's `RollNotePointer`).
//
// Each notice OPENS with its state — "Roll check:" and then the head-count or
// "not completed" — and only those two pieces are bold, so the two notices differ
// from their first words, in print as on screen (view.ts holds the words).
//
// It PRINTS, deliberately: it sits inside the sheet, outside every wrapper
// print.css hides, because a signature attests the count "against this roll" and
// the paper should carry what the screen said about that roll.
//
// Used by the sheet (ResultPage) and by the scrutineer's no-sheet screen, which
// is the one post-count screen that draws no ResultPage.

import type { JSX } from 'react';
import { REGISTRAR_EMAIL } from '../setup/copy';
import {
  ROLL_INTEGRITY_COPY,
  ROLL_INTEGRITY_NOTICE_ID,
  rollIntegrityFoundLead,
  type RollIntegrityView,
} from './view';

export function RollIntegrityNotice({
  integrity,
}: {
  integrity: RollIntegrityView;
}): JSX.Element | null {
  if (integrity.state === 'clean') return null;

  if (integrity.state === 'found') {
    const lead = rollIntegrityFoundLead(integrity.ineligibleOnRoll);
    return (
      <p
        id={ROLL_INTEGRITY_NOTICE_ID}
        tabIndex={-1}
        className="alert alert-warn result-alert result-roll-integrity"
      >
        <strong>{ROLL_INTEGRITY_COPY.label}</strong> <strong>{lead.count}</strong> {lead.rest}{' '}
        {ROLL_INTEGRITY_COPY.foundFollow} <RegistrarAddress />
      </p>
    );
  }

  return (
    <p
      id={ROLL_INTEGRITY_NOTICE_ID}
      tabIndex={-1}
      className="alert alert-info result-alert result-roll-integrity"
    >
      <strong>{ROLL_INTEGRITY_COPY.label}</strong>{' '}
      <strong>{ROLL_INTEGRITY_COPY.uncheckedState}</strong> {ROLL_INTEGRITY_COPY.uncheckedRest}{' '}
      <RegistrarAddress />
    </p>
  );
}

/**
 * The estate's escape hatch (UX-6), from the one constant that names the
 * address.
 *
 * WHERE IT BREAKS, ON PURPOSE (Miranda P1). The visible address is the local
 * part, "@", a `<wbr>`, then the domain — split at the LAST "@" — so a 320px
 * line breaks "registrar@" / "confessinganglicans.nz" rather than mid-word.
 * `overflow-wrap: anywhere` on the link (result.css) stays as the fallback for a
 * domain that is itself wider than the line. The `href` is the whole address,
 * untouched, and a `<wbr>` adds no character, so what is copied or read aloud is
 * the address and nothing else.
 */
function RegistrarAddress(): JSX.Element {
  const at = REGISTRAR_EMAIL.lastIndexOf('@');
  return (
    <a className="result-roll-integrity-address" href={`mailto:${REGISTRAR_EMAIL}`}>
      {REGISTRAR_EMAIL.slice(0, at)}@<wbr />
      {REGISTRAR_EMAIL.slice(at + 1)}
    </a>
  );
}
